/**
 * Environmental Element Colors Store — user-customisable colors per element type
 *
 * @module core/modules/environmental/store/element-colors-store
 *
 * @description
 * Zustand store with localStorage persistence. Keeps only user overrides;
 * falls back to ENVIRONMENTAL_ELEMENT_COLORS defaults for untouched entries.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { ENVIRONMENTAL_ELEMENT_COLORS, type EnvironmentalElementId } from '../constants/element-colors';

interface EnvironmentalElementColorsState {
  /** Only colours the user has explicitly changed */
  overrides: Partial<Record<EnvironmentalElementId, string>>;
}

interface EnvironmentalElementColorsActions {
  /** Return override if set, otherwise default */
  getColor: (id: EnvironmentalElementId) => string;
  /** Set a user override */
  setColor: (id: EnvironmentalElementId, hex: string) => void;
  /** Remove a single override (revert to default) */
  resetColor: (id: EnvironmentalElementId) => void;
  /** Remove all overrides */
  resetAll: () => void;
}

export type EnvironmentalElementColorsStore = EnvironmentalElementColorsState & EnvironmentalElementColorsActions;

export const useEnvironmentalElementColorsStore = create<EnvironmentalElementColorsStore>()(
  devtools(
    persist(
      (set, get) => ({
        overrides: {},

        getColor(id: EnvironmentalElementId): string {
          return get().overrides[id] ?? ENVIRONMENTAL_ELEMENT_COLORS[id];
        },

        setColor(id: EnvironmentalElementId, hex: string): void {
          set(
            (s) => ({ overrides: { ...s.overrides, [id]: hex } }),
            false,
            'setColor',
          );
        },

        resetColor(id: EnvironmentalElementId): void {
          set(
            (s) => {
              const next = { ...s.overrides };
              delete next[id];
              return { overrides: next };
            },
            false,
            'resetColor',
          );
        },

        resetAll(): void {
          set({ overrides: {} }, false, 'resetAll');
        },
      }),
      { name: 'environmental-element-colors' },
    ),
    { name: 'EnvironmentalElementColors' },
  ),
);
